import type {
  ChartData,
  DataSourceInterface,
  Field,
  TableData,
  Statistics,
} from "../data-views/data-source-interface";
import { DataValueType, DateFilter } from "../data-views/options";
import type { Query, View, Feedback } from "../data-views/types";
import type {
FigureData,
FigureDataSourceInterface,
} from "../data-views/figure/figure-data-source-interface";
import { numberFormatter } from "../data-views/chart/labels";
import { t, locale } from "../translations/i18n";

type MonthMap = {
  [key: string]: number;
};

type Datapoint = {
  value: number;
  change: number | null;
  prev: number | null;
  cumulativeValue: number | null;
  cumulativeChange: number | null;
  cumulativePrev: number | null;
  dateFrom: Date;
  dateTo: Date;
  source: string;
  unit: string;
  filter: string[];
};

type TableDatapoint = {
  value: number;
  row: Date;
  column: string;
  statName: string;
  dateFrom: Date;
  dateTo: Date;
  filter: string[];
  verbose_options: {[key: string]: string};
  source: string;
  unit: string | undefined;
};


let production = true;
if (import.meta.env.VITE_DEV == "true") {
  production = false;
}

export class VisitorySource
implements
    DataSourceInterface,
    FigureDataSourceInterface
{

  private api_path: string;
  private token_path: string;
  
  private static readonly monthMap : MonthMap = {
    January: 0,
    February: 1,
    March: 2,
    April: 3,
    May: 4,
    June: 5,
    July: 6,
    August: 7,
    September: 8,
    October: 9,
    November: 10,
    December: 11
  };

  private monthShortList = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May_short",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  private static readonly dataValueTypeMap = {
    [DataValueType.ABSOLUTE]: "value",
    [DataValueType.CHANGE]: "change",
    [DataValueType.PREVIOUS]: "value",
  };
  
  constructor() {
    let language = this.getCurrentLanguage();
    if (production) {
      this.api_path = `https://visitory.io/${language}/dashboard/beta/api/`;
      this.token_path = `https://visitory.io/api/token/`;
    }
    else {
      this.api_path = `http://127.0.0.1:8000/${language}/dashboard/beta/api/`;
      this.token_path = `http://127.0.0.1:8000/api/token/`;
      this.getNewToken();
    }
  }

    public async getExistingCredentials() {
      try {
        if (!production) {
          await this.getNewToken();
        }
        // Check the cookie if the token is expired/empty or not.
        const cookies = document.cookie.split('; ');
        let credentials = {access_token: "", csrftoken: ""};
        cookies.forEach(cookie => {
          const [name, value] = cookie.split('=');
          if (name === "access_token") {
            credentials['access_token'] = value;
          }
          if (name === "csrftoken") {
            credentials['csrftoken'] = value;
          }
        });
        return credentials;
      } catch (error) {
        throw new Error(`Error retrieving token: ${error}`);
      }
    }

    /**
      * DEV ONLY FUNCTION
      * Generation of token is automatic in production, but locally token needs to be generated.
      * Function for generating token.
    */
    public async getNewToken() {

      // If the token is expired/empty, generate a new token. This is just for development.
      let get_token_url = `${this.token_path}`;
      let body = {
        // Add authentication data here.
        username: import.meta.env.VITE_LOCAL_USERNAME,
        password: import.meta.env.VITE_LOCAL_PASSWORD,
      };

      let response = await fetch(get_token_url, {
        method: "POST",
        headers: {
          // Set the content type to JSON
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          // Store the access token in a cookie with an expiration time
          const accessToken = response.access;
          const expirationTime = new Date();
          // Token expires in 2 hours
          expirationTime.setSeconds(expirationTime.getSeconds() + 7200);
          document.cookie = `access_token=${accessToken}; expires=${expirationTime.toUTCString()}; path=/; sameSite=strict;`;
          return response;
        });
      return response.access;
    }

    /**
     * Function for getting different statistics from api for further data queries
    */
    public async getStatistics(): Promise<Statistics> {
      try {
        // API link
        const list_statistics_url: string = `${this.api_path}list_statistics/`

        let credentaials = await this.getExistingCredentials();
        let token = credentaials["access_token"];
        let csrftoken = credentaials["csrftoken"];
        let response = await fetch(list_statistics_url, 
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
              "X-CSRFToken": `${csrftoken}`,
            },
            body: JSON.stringify({}),
          });
      
        let response_data = await response.json();

        return response_data;
      } catch (error) {
          // Handle any errors that occur during the API request
          throw new Error(`Error fetching statistics: ${error}`);
      }
    }

    /**
     * Function for getting fields for certain statistics.
     * This is a final information before getting actual data.
     * @param statName - Model name of the statistics
     */
    public async getFields(statName: string): Promise<Field> {
      try{
        let get_fields_url = `${this.api_path}list_fields/`

        let credentaials = await this.getExistingCredentials();
        let token = credentaials["access_token"];
        let csrftoken = credentaials["csrftoken"];
        let body = JSON.stringify({ model: statName });

        let response = await fetch(get_fields_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "X-CSRFToken": `${csrftoken}`,
          },
          body: body,
        });
    
        let jsonResponse = await response.json();
    
        // Check for JSON parsing errors
        if (!jsonResponse || typeof jsonResponse !== "object") {
          throw new Error("Failed to parse response as JSON");
        }
        return jsonResponse;
      } catch (error) {
        // Handle fetch errors, JSON parsing errors, or other potential errors
        throw new Error(`Error fetching fields`);
      }
    }


    /**
     * Get data from updating "disabled" property on field options.
     */
    public async updateDisabledFields(selectedOptions: any, statName: string): Promise<Field> {

      let token;
      let csrftoken;
      try {
        let credentaials = await this.getExistingCredentials();
        token = credentaials["access_token"];
        csrftoken = credentaials["csrftoken"];
      } catch (tokenError) {
        throw new Error(`Error retrieving token: ${tokenError}`);
      }

      let query_fields = this.formQueryFieldList(selectedOptions);

      // If no fields are selected, get all fields
      if (query_fields === "") {
        return await this.getFields(statName);
      }

      let path: string = `${this.api_path}field_options/`;
      let body = JSON.stringify(
        { 
          query: { 
            model: statName,
            fields: JSON.parse("{" + query_fields + "}")
          } 
      });

      let response = await fetch(path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "X-CSRFToken": `${csrftoken}`,
        },
        body: body,
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch data. Status: ${response.status} ${response.statusText}`);
      }
      let jsonResponse = await response.json();
      return jsonResponse;
    }

  /**
   * Get Chart data from API.
   */
  public async getChartData(query: Query): Promise<ChartData> {
    try {
      return await this._getChartData(query);
    } catch (error) {
      throw new Error(`Error while fetching chart data: ${error}`);
    }
  }

  private async _getChartData(query: Query): Promise<ChartData> {
    try {
      let _no_aggregation;
      if (query.dateFilter[0] == DateFilter.MONTHS) {
        _no_aggregation = true;
      } else {
        _no_aggregation = false;
      }
      // Call getDataFromApi function to get data from api.
      let json_data = await this.getDatapointsFromApi(query, _no_aggregation);
      let jsonObject = JSON.parse(json_data);

      // Check for empty data.(with response code 204 - No Content)
      if (Object.keys(jsonObject).length === 0){  
        const queryData = new Promise<ChartData>((resolve) => {
          const emptyChartData: ChartData = [];
          resolve(emptyChartData);
        });
        return queryData;
      }

      // Check for JSON parsing errors
      if (!jsonObject || !jsonObject.hasOwnProperty("columns") || !jsonObject.hasOwnProperty("data") || !jsonObject.hasOwnProperty("index")) {
        throw new Error("Invalid data received from API"); // Handle invalid data from API
      }

      // Create an empty array to store the Datapoint objects
      let datapoints: Datapoint[] = [];

      // Loop through the arrays and create a Datapoint object for each set of values
      for (let i = 0; i < jsonObject["columns"].length; i++) {
        const datapoint: Datapoint = {
          value: jsonObject["data"][jsonObject["index"].indexOf('val')][i],
          change: jsonObject["data"][jsonObject["index"].indexOf('change')][i],
          prev: (i == 0) ? null : jsonObject["data"][jsonObject["index"].indexOf('val')][i-1],
          cumulativeValue: null,
          cumulativeChange: null,
          cumulativePrev: null,
          dateFrom: new Date(jsonObject["data"][jsonObject["index"].indexOf('min_date')][i]),
          dateTo: new Date(jsonObject["data"][jsonObject["index"].indexOf('max_date')][i]),
          source: jsonObject["data"][jsonObject["index"].indexOf('source')][i],
          unit: jsonObject["data"][jsonObject["index"].indexOf('unit')][i],
          filter: query.dateFilter,
        };

        // Add the Datapoint object to the array of datapoints
        datapoints.push(datapoint);
      }

      if (query.seriesOptions.is_daily){ // Calculate cumulative data for daily data
        if (query.dateFilter[0] == DateFilter.MONTHS) {
          console.log("ToDo: calculate monthly data");
        } else if (query.dateFilter[0] == DateFilter.YEARS) {
          datapoints = this.calculateCumulativeChart(datapoints);
          datapoints = this.calculateChange(datapoints);
        } else if(query.dateFilter.length == 2){
          console.log("ToDo: calculate data for custom period");
        } else {
          // Create monthly data for daily data
          const month = VisitorySource.monthMap[query.dateFilter[0]];
          datapoints = this.selectByMonth(datapoints, month);
          datapoints = this.calculateMonthlyData(datapoints);
        }
      } else { // Calculate cumulative data for monthly data
        if (query.dateFilter[0] == DateFilter.MONTHS) {
          datapoints = this.sortByMonth(datapoints);
          datapoints = this.calculateChange(datapoints);
          datapoints = this.sortByDate(datapoints);
        } else if (query.dateFilter[0] == DateFilter.YEARS) {
          datapoints = this.calculateChange(datapoints);
        } else if(query.dateFilter.length == 2){
          datapoints = this.calculateChange(datapoints);
        } else {
          const month = VisitorySource.monthMap[query.dateFilter[0]];
          datapoints = this.selectByMonth(datapoints, month);
          datapoints = this.calculateChange(datapoints);
        }
      }

      const queryData = datapoints.map((datapoint) => {
        return {
          value:
            datapoint[
              VisitorySource.dataValueTypeMap[query.dataValueType] as keyof Pick<
                Datapoint,
                "value" | "change" | "prev"
              >
            ],
          date: datapoint.dateFrom,
          unit: datapoint.unit,
          source: datapoint.source,
        };
      });
      return queryData;
    } catch (error) {
      throw new Error(`Error in _getData: ${error}`); // Rethrow the error for further handling or propagation
    }
  }

  private sortByMonth = (datapoints: Datapoint[]): Datapoint[] => {
    return datapoints.sort((a, b) => {
      return a.dateFrom.getMonth() - b.dateFrom.getMonth();
    });
  };

  private sortByMonthTable = (tableDatapoint: TableDatapoint[]): TableDatapoint[] => {
    return tableDatapoint.sort((a, b) => {
      return a.dateFrom.getMonth() - b.dateFrom.getMonth();
    });
  };

  private sortByDate = (datapoints: Datapoint[]): Datapoint[] => {
    return datapoints.sort((a, b) => {
      return a.dateFrom.getTime() - b.dateFrom.getTime();
    });
  };

  private sortByDateTable = (tableDatapoint: TableDatapoint[]): TableDatapoint[] => {
    return tableDatapoint.sort((a, b) => {
      return a.dateFrom.getTime() - b.dateFrom.getTime();
    });
  };

  private selectByMonth = (
    datapoints: Datapoint[],
    month: number
  ): Datapoint[] => {
    return datapoints.filter((datapoint) => {
      return datapoint.dateFrom.getMonth() == month;
    });
  };

  private selectByMonthTable = (
    datapoints: TableDatapoint[],
    month: number
  ): TableDatapoint[] => {
    return datapoints.filter((datapoint) => {
      return datapoint.dateFrom.getMonth() == month;
    });
  };

  public calculateChange = (datapoints: Datapoint[]): Datapoint[] => {
    return datapoints.map((datapoint, index) => {
      const prevDatapoint = datapoints[index - 1];

      if (!prevDatapoint) return datapoint;

      if (
        prevDatapoint?.dateFrom.getMonth() !== datapoint?.dateFrom.getMonth()
      ) {
        return {
          ...datapoint,
          change: null,
          prev: null,
        };
      }

      let change = null;
      if (datapoint.value !== null && prevDatapoint.value) {
        change = (datapoint.value - prevDatapoint.value) / prevDatapoint.value;
      }

      let cumulativeChange = null;
      if (datapoint.cumulativeValue !== null && prevDatapoint.cumulativeValue) {
        cumulativeChange =
          (datapoint.cumulativeValue - prevDatapoint.cumulativeValue) /
          prevDatapoint.cumulativeValue;
      }

      return {
        ...datapoint,
        change: change,
        prev: prevDatapoint.value,
        cumulativeChange: cumulativeChange,
        cumulativePrev: prevDatapoint.cumulativeValue,
      };
    });
  };


  /**
   * Calculate monthly data from daily data.
   */
  public calculateMonthlyData(datapoints: Datapoint[]): Datapoint[] {
    const monthlyData: Datapoint[] = [];
    const monthlySums: Map<string, number> = new Map();
    let monthKey: string;
    let sum: number;
  
    datapoints.forEach((datapoint, index) => {
      if (!datapoint || typeof datapoint !== "object" || !datapoint.dateFrom) {
        throw new Error(`Invalid datapoint at index ${index}.`);
      }
  
      const prevDatapoint = datapoints[index - 1];
  
      monthKey = `${datapoint.dateFrom.getFullYear()}-${datapoint.dateFrom.getMonth()}`;
      sum = monthlySums.get(monthKey) || 0;
      monthlySums.set(monthKey, sum + datapoint.value);
  
      if (
        !prevDatapoint ||
        prevDatapoint.dateFrom.getMonth() !== datapoint.dateFrom.getMonth() ||
        prevDatapoint.dateFrom.getFullYear() !== datapoint.dateFrom.getFullYear()
      ) {
        const monthStartDate = new Date(Date.UTC(
          datapoint.dateFrom.getFullYear(),
          datapoint.dateFrom.getMonth(),
          1
        ));
        const monthEndDate = new Date(Date.UTC(
          datapoint.dateFrom.getFullYear(),
          datapoint.dateFrom.getMonth() + 1,
          0
        ));
        const monthlyEntry: Datapoint = {
          ...datapoint,
          dateFrom: monthStartDate,
          dateTo: monthEndDate,
          value: 0,
        };
        monthlyData.push(monthlyEntry);
      }
    });
    monthlyData.forEach((datapoint, index) => {
      monthKey = `${datapoint.dateFrom.getFullYear()}-${datapoint.dateFrom.getMonth()}`;
      datapoint.value = monthlySums.get(monthKey)!;
    })
    return monthlyData;
  }

  /**
   * Calculates cumulative yearly values and changes.
   *
   * NOTE: Nulls are treated as zeros
   *
   * @param datapoints
   * @returns
   */
  private calculateCumulativeChart = (datapoints: Datapoint[]): Datapoint[] => {

    if (!Array.isArray(datapoints)) {
      throw new Error("Input datapoints must be an array.");
    }
    if (datapoints[0].filter.length != 2 || VisitorySource.monthMap[datapoints[0].filter[0]] < VisitorySource.monthMap[datapoints[0].filter[1]]){
      return this.calculateCulumalativeChartWithinYear(datapoints);
    } else {
      return this.calculateCumulativeChartOverYear(datapoints);
    }
  };
  

  /**
   * If selected custom period goes over a year (E.g. Dec - Feb), calculate cumulative values over the year.
   */
  public calculateCumulativeChartOverYear = (datapoints: Datapoint[]): Datapoint[] => {
    const cumulativeDatapoints: Datapoint[] = [];
    let cumulative_value = 0;
    const filteredMonthsList = this.getMonthsBetween(datapoints[0].filter[0], datapoints[0].filter[1]);
    const lastMonth = filteredMonthsList[filteredMonthsList.length - 1];
  
    datapoints.forEach((datapoint, index) => {
      if (datapoint.dateFrom.getMonth() == VisitorySource.monthMap[lastMonth] || index == datapoints.length - 1) {
        cumulative_value += datapoint.value;
        if (index === datapoints.length - 1) {
          let lastYear = datapoint.dateFrom.getFullYear();
          if (datapoints[index - 2].dateFrom.getFullYear() == lastYear){ 
            lastYear = lastYear + 1;
          }
          const updatedDateFrom = new Date(datapoint.dateFrom);
          updatedDateFrom.setFullYear(lastYear);
          
          // Set month to Jan since otherwise the last datapoint
          // might be set incorrectly if data is insufficient
          updatedDateFrom.setMonth(0);
          cumulativeDatapoints.push({
            ...datapoint,
            dateFrom: new Date(updatedDateFrom),
            value: cumulative_value,
          });
        } else {
          cumulativeDatapoints.push({
            ...datapoint,
            dateFrom: new Date(Date.UTC(datapoint.dateFrom.getFullYear(), 0)),
            value: cumulative_value,
          });
        }
        cumulative_value = 0;
      } else {
        cumulative_value += datapoint.value;
      }
    });
  
    const reIndexedCumulativeDatapoints = cumulativeDatapoints.filter((n) => n);
    return reIndexedCumulativeDatapoints;
  };
  

  /**
   * If the selected custom period is within a year (E.g. Jan - May), calculate cumulative values within the year.
   */
  public calculateCulumalativeChartWithinYear = (datapoints: Datapoint[]): Datapoint[] => {
    const cumulativeDatapoints: Datapoint[] = [];
    let cumulative_value = 0;
    
    datapoints.forEach((datapoint, index) => {
      if (!datapoint || typeof datapoint !== "object" || !datapoint.dateFrom) {
        throw new Error(`Invalid datapoint at index ${index}.`);
      }
      const prevDatapoint = datapoints[index - 1];
      cumulative_value += datapoint.value;

      if (
        !prevDatapoint ||
        prevDatapoint.dateFrom.getFullYear() != datapoint.dateFrom.getFullYear()
      )
      {
        cumulative_value = datapoint.value;
        return;
      }
      cumulativeDatapoints[datapoint.dateFrom.getFullYear()] = {
        ...datapoint,
        dateFrom: new Date(Date.UTC(datapoint.dateFrom.getFullYear(), 0)),
        value: cumulative_value,
      };
    });

    const reIndexedCumulativeDatapoints = cumulativeDatapoints.filter((n) => n);
    return reIndexedCumulativeDatapoints;
  };

  /**
   * Get Table data from API.
   */
  public async getTableData(query: Query): Promise<TableData> {
    try {
      return await this._getTableData(query);
    } catch (error) {
      throw new Error(`Error while fetching table data: ${error}`);
    }
  }

  private async _getTableData(query: Query): Promise<TableData> {
   try {

      let _no_aggregation;
      if (query.dateFilter[0] == DateFilter.MONTHS) {
        _no_aggregation = true;
      } else {
        _no_aggregation = false;
      }
      // Call getDataFromApi function to get data from api.
      let json_data = await this.getDatapointsFromApi(query, _no_aggregation);

      if (json_data !== "{}") {

        let currentLanguage = this.getCurrentLanguage();
        let jsonObject = JSON.parse(json_data);
        
        // Create an empty array to store the Datapoint objects
        let table_datapoints: TableDatapoint[] = [];

        // Loop through the arrays and create a Datapoint object for each set of values
        for (let i = 0; i < jsonObject["columns"].length; i++) {
            const datapoint: TableDatapoint = {
              column: this._getVerboseOptionByEnOption(query.seriesOptions.verbose_options, query.seriesOptions.options.location, currentLanguage),
              row : new Date(jsonObject["data"][jsonObject["index"].indexOf('min_date')][i]),
              value: jsonObject["data"][jsonObject["index"].indexOf('val')][i],
              statName: query.seriesOptions.statVerboseName[currentLanguage],
              dateFrom: new Date(jsonObject["data"][jsonObject["index"].indexOf('min_date')][i]),
              dateTo: new Date(jsonObject["data"][jsonObject["index"].indexOf('max_date')][i]),
              filter: query.dateFilter,
              verbose_options: query.seriesOptions.verbose_options,
              unit: jsonObject["data"][jsonObject["index"].indexOf('unit')][i],
              source: jsonObject["data"][jsonObject["index"].indexOf('source')][i]
            };
          // Add the Datapoint object to the array of datapoints
          table_datapoints.push(datapoint);
        }

        // Sort the datapoints by date
        if (query.seriesOptions.is_daily){
          table_datapoints = table_datapoints;
        } else {
          if (query.dateFilter[0] == DateFilter.MONTHS) {
            table_datapoints = this.sortByMonthTable(table_datapoints);
            table_datapoints = this.sortByDateTable(table_datapoints);
          } else if (query.dateFilter[0] == DateFilter.YEARS) {
            table_datapoints = this.sortByDateTable(table_datapoints);
          } else {
            const month = VisitorySource.monthMap[query.dateFilter[0]];
            table_datapoints = this.selectByMonthTable(table_datapoints, month);
          }
        }
        
        let rowName: any;
        
        // Create an array of objects with the required format
        const queryData = table_datapoints.map((datapoint) => {
          if (query.seriesOptions.is_daily){
            rowName = String(datapoint.row.getDate() + " " + this.translate(this.monthShortList[datapoint.row.getMonth()]) + ", " + datapoint.row.getFullYear());
          } else {
            if (query.dateFilter[0] == "Months") {
              rowName = String(this.translate(this.monthShortList[datapoint.row.getMonth()]) + " " + datapoint.row.getFullYear());     
            }
            else if (query.dateFilter[0] == "Years") {
              rowName = String(datapoint.row.getFullYear());
            } else if (query.dateFilter.length == 2) {
              if (VisitorySource.monthMap[query.dateFilter[0]] < VisitorySource.monthMap[query.dateFilter[1]]){
                rowName = String(this.translate(this.monthShortList[VisitorySource.monthMap[datapoint.filter[0]]])) + " - " + String(this.translate(this.monthShortList[VisitorySource.monthMap[datapoint.filter[1]]]))+ ", " + datapoint.dateFrom.getFullYear();
              } else {
                rowName = String(this.translate(this.monthShortList[VisitorySource.monthMap[datapoint.filter[0]]]) + " " + (datapoint.dateFrom.getFullYear())) + " - " + String(this.translate(this.monthShortList[VisitorySource.monthMap[datapoint.filter[1]]]) + " " + datapoint.dateTo.getFullYear());
              }
            } else {
              rowName = String(this.translate(this.monthShortList[datapoint.row.getMonth()]) + " " + datapoint.row.getFullYear());
            }
          }

          let datapointValue = numberFormatter(datapoint.value);

          if ((datapoint.unit != "" || datapoint.unit != null || datapoint.unit != undefined) && datapointValue != "") {
            datapointValue = `${numberFormatter(datapoint.value)} ${datapoint.unit}`
          }

          return {
            value: datapointValue,
            dateFrom: datapoint.dateFrom,
            dateTo: datapoint.dateTo,
            row: rowName,
            column: datapoint.column,
            statName: datapoint.statName,
            verbose_options: datapoint.verbose_options,
            id: undefined,
            source: datapoint.source
          };
        });
        return queryData;
      }
      else { 
        return [];
      }
    } catch (error) {
      throw new Error(`Error in _getTableData: ${error}`);
    }
  }

  /**
   * Calculates cumulative yearly values and changes for Tables Data.
   *
   * NOTE: Nulls are treated as zeros
   *
   * @param datapoints
   * @returns
   */
  private calculateCumulativeTable = (datapoints: TableDatapoint[]): TableDatapoint[] => {

    if (!Array.isArray(datapoints)) {
      throw new Error("Input datapoints must be an array.");
    }

    if (datapoints[0].filter.length != 2 || VisitorySource.monthMap[datapoints[0].filter[0]] < VisitorySource.monthMap[datapoints[0].filter[1]]){
      return this.calculateCulumalativeTableWithinYear(datapoints);
    } else {
      return this.calculateCumulativeTableOverYear(datapoints);
    }
  };
  

  /**
   * If selected custom period goes over a year (E.g. Dec - Feb), calculate cumulative values over the year.
   */
  public calculateCumulativeTableOverYear = (datapoints: TableDatapoint[]): TableDatapoint[] => {
    const cumulativeDatapoints: TableDatapoint[] = [];
    let cumulative_value = 0;
    const filteredMonthsList = this.getMonthsBetween(datapoints[0].filter[0], datapoints[0].filter[1]);
    const lastMonth = filteredMonthsList[filteredMonthsList.length - 1];
  
    datapoints.forEach((datapoint, index) => {
      if (datapoint.dateFrom.getMonth() == VisitorySource.monthMap[lastMonth] || index == datapoints.length - 1) {
        cumulative_value += datapoint.value;
        if (index === datapoints.length - 1) {
          let lastYear = datapoint.dateFrom.getFullYear();
          if (datapoints[index - 2].dateFrom.getFullYear() == lastYear){ 
            lastYear = lastYear + 1;
          }
          const updatedDateFrom = new Date(datapoint.row);
          updatedDateFrom.setFullYear(lastYear);
          cumulativeDatapoints.push({
            ...datapoint,
            row: new Date(updatedDateFrom),
            value: cumulative_value,
          });
        } else {
          cumulativeDatapoints.push({
            ...datapoint,
            row: new Date(Date.UTC(datapoint.dateFrom.getFullYear(), 0)),
            value: cumulative_value,
          });
        }
        cumulative_value = 0;
      } else {
        cumulative_value += datapoint.value;
      }
    });
  
    const reIndexedCumulativeDatapoints = cumulativeDatapoints.filter((n) => n);
    return reIndexedCumulativeDatapoints;
  };
  

  /**
   * Function for getting a list of months between two months.
   */
  private getMonthsBetween(startMonth: string, endMonth: string): string[] {
    const allMonths = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
  
    const startIndex = allMonths.findIndex((month) => month === startMonth);
    const endIndex = allMonths.findIndex((month) => month === endMonth);
  
    if (startIndex === -1 || endIndex === -1) {
      return [];
    }
  
    let monthsBetween: string[];
  
    if (endIndex >= startIndex) {
      monthsBetween = allMonths.slice(startIndex, endIndex + 1);
    } else {
      monthsBetween = allMonths.slice(startIndex).concat(allMonths.slice(0, endIndex + 1));
    }
  
    return monthsBetween;
  }

  /**
   * If the selected custom period is within a year (E.g. Jan - May), calculate cumulative values within the year.
   */
  public calculateCulumalativeTableWithinYear = (datapoints: TableDatapoint[]): TableDatapoint[] => {
    const cumulativeDatapoints: TableDatapoint[] = [];
    let cumulative_value = 0;
    
    datapoints.forEach((datapoint, index) => {
      if (!datapoint || typeof datapoint !== "object" || !datapoint.row) {
        throw new Error(`Invalid datapoint at index ${index}.`);
      }
      const prevDatapoint = datapoints[index - 1];
      cumulative_value += datapoint.value;

      if (
        !prevDatapoint ||
        prevDatapoint.row.getFullYear() != datapoint.row.getFullYear()
      )
      {
        cumulative_value = datapoint.value;
        return;
      }
      cumulativeDatapoints[datapoint.row.getFullYear()] = {
        ...datapoint,
        row: new Date(Date.UTC(datapoint.row.getFullYear(), 0)),
        value: cumulative_value,
      };
    });

    const reIndexedCumulativeDatapoints = cumulativeDatapoints.filter((n) => n);
    return reIndexedCumulativeDatapoints;
  };

  /**
   * Get Figure data from API.
   * @param query 
   */
  public async getFigureData(query: Query, aggregation: boolean = false): Promise<FigureData> {
    return new Promise((resolve) => {
      resolve(this._getFigureData(query, aggregation));
    });
  }

  private async _getFigureData(query: Query, aggregation: boolean = false): Promise<FigureData> {
    let _no_aggregation;
    if (query.dateFilter[0] == DateFilter.MONTHS) {
      _no_aggregation = true;
    } else {
      _no_aggregation = false;
    }
    // Call getDataFromApi function to get data from api.
    let json_data = await this.getDatapointsFromApi(query, _no_aggregation);

    let currentLanguage = this.getCurrentLanguage();
    let jsonObject = JSON.parse(json_data);

    let lastDatapointIndex;

    let value: Array<any> = [];
    let change: Array<any> = [];
    let unit: Array<any> = [];
    let date: Array<any> = [];
    let statName;
    let source: Array<string> = [];

    if (aggregation){
      // Temporary solution for aggregation copied from getChartData
      // This functionality should be moved from the frontend to the backend later

      // Create an empty array to store the Datapoint objects
      let datapoints: Datapoint[] = [];

      // Loop through the arrays and create a Datapoint object for each set of values
      for (let i = 0; i < jsonObject["columns"].length; i++) {
        const datapoint: Datapoint = {
          value: jsonObject["data"][jsonObject["index"].indexOf('val')][i],
          change: jsonObject["data"][jsonObject["index"].indexOf('change')][i],
          prev: (i == 0) ? null : jsonObject["data"][jsonObject["index"].indexOf('val')][i-1],
          cumulativeValue: null,
          cumulativeChange: null,
          cumulativePrev: null,
          dateFrom: new Date(jsonObject["data"][jsonObject["index"].indexOf('min_date')][i]),
          dateTo: new Date(jsonObject["data"][jsonObject["index"].indexOf('max_date')][i]),
          source: jsonObject["data"][jsonObject["index"].indexOf('source')][i],
          unit: jsonObject["data"][jsonObject["index"].indexOf('unit')][i],
          filter: query.dateFilter,
        };

        // Add the Datapoint object to the array of datapoints
        datapoints.push(datapoint);
      }

      if (query.seriesOptions.is_daily){ // Calculate cumulative data for daily data
        console.log("ToDo: calculate daily aggregation");
      } else { // Calculate cumulative data for monthly data
        if (query.dateFilter[0] == DateFilter.MONTHS) {
          datapoints = this.sortByMonth(datapoints);
          datapoints = this.sortByDate(datapoints);
        } else {
          const month = VisitorySource.monthMap[query.dateFilter[0]];
          datapoints = this.selectByMonth(datapoints, month);
        }
      }
      datapoints.forEach((datapoint) => {
        value.push(datapoint.value);
        change.push(datapoint.change);
        unit.push(datapoint.unit);
        date.push(datapoint.dateFrom);
        statName = query.seriesOptions.statVerboseName[currentLanguage];
        source.push(datapoint.source);
      });

      lastDatapointIndex = datapoints.length - 1;
    }
    else {
      lastDatapointIndex = jsonObject["columns"].length - 1;

      value = jsonObject["data"][jsonObject["index"].indexOf('val')];
      change = jsonObject["data"][jsonObject["index"].indexOf('change')];
      unit = jsonObject["data"][jsonObject["index"].indexOf('unit')];
      date = jsonObject["data"][jsonObject["index"].indexOf('min_date')];
      source = jsonObject["data"][jsonObject["index"].indexOf('source')]
    }

    statName = query.seriesOptions.statVerboseName[currentLanguage];

    return {
      values: value,
      changes: change,
      units: unit,
      dates: date,
      statName: statName,
      lastDatapointIndex: lastDatapointIndex,
      source: new Set(source)
    };
  }

  /**
         * Function for get statistics data from visitory api.
  */
  public async getDatapointsFromApi(query: Query, _no_aggregation: boolean = true): Promise<any> {
    try {
      let statName = query.seriesOptions["statName"];
      let query_fields = this.formQueryFields(query.seriesOptions.options)
      let dateRange = this.getDateRange(query);
      let token;
      let csrftoken;
      try {
        let credentaials = await this.getExistingCredentials();
        token = credentaials["access_token"];
        csrftoken = credentaials["csrftoken"];
      } catch (tokenError) {
        throw new Error(`Error retrieving token: ${tokenError}`);
      }

      const path: string = `${this.api_path}data/`;

      let fields = JSON.parse(`{"_no_aggregation":${_no_aggregation},"_min":${dateRange.min},"_max":${dateRange.max},${query_fields}}`)

      let body = JSON.stringify({
        query: {
          model: statName,
          fields: fields,
        },
        logged_in: true
      });

      let response = await fetch(path, 
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "X-CSRFToken": `${csrftoken}`,
          },
          body: body,
        });

      if (!response.ok) {
        throw new Error(`Failed to fetch data. Status: ${response.status} ${response.statusText}`);
      }
      
      // If response is empty, return empty json.
      if (response.status === 204) {
        const emptyResponse = {};
        const json = JSON.stringify(emptyResponse);
        return json;
      }
      else {
        let jsonResponse;
      try {
        jsonResponse = JSON.parse(await response.text());
      } catch (error) {
        throw new Error(`Error parsing response JSON`)
      }
      return jsonResponse;
      }
    } catch (error) {
      throw new Error(`Error in getDatapointsFromApi: ${error}`);
    }
  }


  /**
      * Function for saving or updating view to visitory database.
  */
  public async saveView(view: View, updateMode: boolean = false, new_name: string = "") {
    try{
      const manage_views_url: string = `${this.api_path}manage_views/`

      let credentaials = await this.getExistingCredentials();
      let token = credentaials["access_token"];
      let csrftoken = credentaials["csrftoken"];
      let action = updateMode ? "update" : "save";
      let body: any = 
        {
          query: { 
            "view_name": view.name,
            "latest_update": view.latest_update,
            "action": action,
            "view_data": view.view,
          },
          logged_in: true
      };

      // Add new_name to body if it's provided
      if (new_name !== "") {
        body.query["new_name"] = new_name;
      }

      let response = await fetch(manage_views_url, 
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "X-CSRFToken": `${csrftoken}`,
          },
          body: JSON.stringify(body),
        });

      return response;
  
    } catch (error) {
        // Handle any errors that occur during the API request
        throw new Error(`Error saving view: ${error}`);
    }
  }

  /**
         * Function for saving view to visitory database.
  */
  /**
     * Function for getting different statistics from api for further data queries
    */
  public async getViewEntries(): Promise<any> {
    try{
      const manage_views_url: string = `${this.api_path}manage_views/`

      let credentaials = await this.getExistingCredentials();
      let token = credentaials["access_token"];
      let csrftoken = credentaials["csrftoken"];
      let body = JSON.stringify(
        { 
          query: { 
            "action": "get_views"
          },
          logged_in: true
      });
      let response = await fetch(manage_views_url, 
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "X-CSRFToken": `${csrftoken}`,
          },
          body: body,
        });
    
      let response_data = await response.json();

      return response_data;
  
    } catch (error) {
        // Handle any errors that occur during the API request
        throw new Error(`Error fetching view entries: ${error}`);
    }
  }

  public updateView(view: View) { 
    return 'success';
  }

  public async deleteView(viewName: string) { 
    try{
      const manage_views_url: string = `${this.api_path}manage_views/`

      let credentaials = await this.getExistingCredentials();
      let token = credentaials["access_token"];
      let csrftoken = credentaials["csrftoken"];
      let body = JSON.stringify(
        { 
          query: { 
            "action": "delete",
            "view_name": viewName
          },
          logged_in: true
      });
      let response = await fetch(manage_views_url, 
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "X-CSRFToken": `${csrftoken}`,
          },
          body: body,
        });

      return response;
  
    } catch (error) {
        // Handle any errors that occur during the API request
        throw new Error(`Error fetching statistics: ${error}`);
    }
  }

/**
 * Forms proper string of query options from options object for further use in API query
 * 
 * @param options
 * @returns
 */
  public formQueryFields(options: Object){
    // Divide options into fields and field_values
    let fields = Object.keys(options);
    let field_values = Object.values(options);

    // Capitalize first letter of each field
    for (let i = 0; i < fields.length; i++) {
      fields[i] = fields[i].charAt(0).toUpperCase() + fields[i].slice(1);
    }

    // Form query_fields string
    let query_fields = "";
    for (let i = 0; i < fields.length; i++) {

      // Check for null or undefined values
      if (field_values[i] === null || field_values[i] === undefined) {
        throw new Error(`Invalid value. Field "${fields[i]}" has a null or undefined value.`);
      }

      if (i == fields.length - 1) {
        query_fields += `"${fields[i]}":"${field_values[i].toLowerCase()}"`;
      }
      else{
        // If last field, don't add comma
        query_fields += `"${fields[i]}":"${field_values[i].toLowerCase()}",`;
      }
    }
    return query_fields;
  }

  /**
   * Function for getting list of fields for certain statistics.
   * The query field list will be used to disable certain options in the dropdown
   */
  public formQueryFieldList(input: any){

    const result: string[] = [];

    for (const key in input) {
      if (input[key].length !== 0 && input.hasOwnProperty(key)) {
        const field_properties = input[key];
        const formattedValues = field_properties["values_db"].map((value: any) => `"${value}"`);
        const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
        const formattedString = `"${formattedKey}":[${formattedValues.join(",")}]`;
        result.push(formattedString);
      }
    }
  let queryFields = `${result.join(",")}`;

  return queryFields;
  }

  /**
   * Function for checking what month range should we get from the api.
   */
  public getDateRange(query: Query) {

    let dateRange = {
      min: 0,
      max: 0,
    };
  if (query.dateFilter.length == 2){
    dateRange.min = VisitorySource.monthMap[query.dateFilter[0]] + 1;
    dateRange.max = VisitorySource.monthMap[query.dateFilter[1]] + 1;
  } else if (query.dateFilter[0] == "Months" || query.dateFilter[0] == "Years"){
      dateRange.min = 1;
      dateRange.max = 12;
  } else {
      dateRange.min = VisitorySource.monthMap[query.dateFilter[0]] + 1;
      dateRange.max = dateRange.min;
  }
   return dateRange;
  }

  /**
   * Translate a string
   *
   * @param input - String to translate
   */
  public translate(input: string): string {
    let translatedText = '';

    t.subscribe((translationFunction) => {
      translatedText = translationFunction(input);
    });
    return translatedText;
  }

  /**
    * Get Current language
  */
  public getCurrentLanguage() {
    let currentLanguage = '';
    locale.subscribe(value => {
      currentLanguage = value;
    });
    return currentLanguage.split("-")[0];
  }

  /**
   * This function gets the translated option value from the verbose_options object by untraslated option value.
   * @param verbose_options 
   * @param option_en 
   * @param currentLanguage 
   * @returns 
   */
  public _getVerboseOptionByEnOption(verbose_options: { [key: string]: any }, option_en: string, currentLanguage: string) {
    let translated_option;
    for (const key in verbose_options) {
      const translation = verbose_options[key];
      if (Object.values(translation).includes(option_en)) {
        translated_option = translation[currentLanguage];
        return translated_option;
      }
    }
  }

  /** 
   * Function for sending feedback to the API.
   */
  public async sendFeedback(feedback: Feedback) {
    try {
      // API link
      const send_feedback_url: string = `${this.api_path}send_feedback/`

      let credentaials = await this.getExistingCredentials();
      let token = credentaials["access_token"];
      let csrftoken = credentaials["csrftoken"];
      let feedbackBody = {"feedback": feedback};
      let body = JSON.stringify(feedbackBody);

      let response = await fetch(send_feedback_url, 
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "X-CSRFToken": `${csrftoken}`,
          },
          body: body,
        });

      return response.status;
    } catch (error) {
        throw new Error(`Error sending feedback: ${error}`);
    }
  }
}
